import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "components/translations/useTranslation";
import {
    ExceptionForm,
    buildExceptionRow,
    isExceptionSaveEnabled,
} from "components/modals/ExceptionForm.component";
import {
    AddExceptionProps,
    ExceptionRoleOption,
    ExceptionUserOption,
} from "types/modals";

const AddException = ({ more, closeModal, loopFocus }: AddExceptionProps): React.ReactElement => {
    const { translate } = useTranslation();

    const { noDuplicates, rows, hideClose, handleAction } = more;

    const [requester, setRequester] = useState<ExceptionUserOption | undefined>(undefined);
    const [role, setRole] = useState<ExceptionRoleOption | undefined>(undefined);
    const [approvers, setApprovers] = useState<ExceptionUserOption[] | undefined>(undefined);
    const [focusRequester, setFocusRequester] = useState<boolean>(false);

    useEffect(() => {
        setFocusRequester(true);
    }, []);

    const filterValues = useMemo(
        () => (noDuplicates ? (rows || []).map((row) => row.value) : []),
        [noDuplicates, rows],
    );

    const handleRequesterChange = useCallback((value: ExceptionUserOption | null) => {
        setRequester(value ?? undefined);
        setFocusRequester(false);
    }, []);

    const handleRoleChange = useCallback((value: ExceptionRoleOption | null) => {
        setRole(value ?? undefined);
    }, []);

    const handleApproversChange = useCallback((value: ExceptionUserOption[] | null) => {
        setApprovers(value ?? undefined);
    }, []);

    const handleOk = useCallback(() => {
        handleAction(buildExceptionRow(requester, role, approvers));
        closeModal();
    }, [requester, role, approvers, handleAction, closeModal]);

    return (
        <ExceptionForm
            translate={translate}
            requester={requester}
            role={role}
            approvers={approvers}
            filterValues={filterValues}
            onRequesterChange={handleRequesterChange}
            onRoleChange={handleRoleChange}
            onApproversChange={handleApproversChange}
            focusRequester={focusRequester}
            hideClose={hideClose}
            okText={translate("workflowDetails.exceptions.addException")}
            isSaveEnabled={isExceptionSaveEnabled(requester, role, approvers)}
            onOk={handleOk}
            onCancel={closeModal}
            loopFocus={loopFocus}
        />
    );
};

export default AddException;
